import { ApiEndpoint } from '@/api/registry';
import { useApiQuery } from '@/api/useApiQuery';
import type {
  FetchRunResponse,
  ModelDataForecastResponse,
  ModelOutputRecord,
} from '@/features/forecasts/forecasts.api.types';
import { useTenant } from '@/providers/TenantProvider';

type Params = {
  solutionId: string | null;
  primaryKey: Record<string, string>;
  leadtimeSpans?: Array<[number, number]>;
  enabled?: boolean;
};

// Stable empty reference so callers' identity checks don't churn each render.
const EMPTY_FORECAST: ModelOutputRecord[] = [];

export function useForecastRunOutputs({ solutionId, primaryKey, leadtimeSpans, enabled = true }: Params) {
  const { tenantAlias } = useTenant();

  const runResult = useApiQuery<FetchRunResponse>(
    tenantAlias,
    ApiEndpoint.SolutionsFetchRun,
    { pathParams: { solution_id: solutionId ?? '' } },
    { enabled: enabled && Boolean(solutionId) },
  );
  const runId = runResult.data?.run_id ?? null;
  const runPending = enabled && Boolean(solutionId) && !runId;

  const forecastResult = useApiQuery<ModelDataForecastResponse>(
    tenantAlias,
    ApiEndpoint.ModelOutputsForecast,
    {
      body: {
        run_id: runId ?? '',
        file_id: 'forecast.parquet',
        primary_keys: primaryKey,
        ...(leadtimeSpans !== undefined ? { leadtime_spans: leadtimeSpans } : {}),
      },
    },
    { enabled: enabled && Boolean(runId) && !runPending },
  );

  return {
    forecast: forecastResult.data?.results ?? EMPTY_FORECAST,
    isLoading: runResult.isLoading || runPending || forecastResult.isLoading,
    isError: runResult.isError || forecastResult.isError,
  };
}
